import type { Background, Shadow, Watermark } from "@/common/components/FileCenterProvider";
import { StrokeProps } from "@/common/components/FileCenterProvider";
import { FileInfo } from "../services/FileCenter";
import { getString, getTextWH } from "./utils";
import { PositionType } from "@/common/constants/interfaces";
import { getPositionLeftTop } from "./positionUtil";

export interface DrawContext {
    canvas: HTMLCanvasElement | null | undefined;
    ctx: CanvasRenderingContext2D | null | undefined;
    width: number; // canvas css width
    height: number; // canvas css height
    dpr: number;
    ratio: number; // 预览相当于原图的缩放比例
    isCollect: boolean; // 是否为后台批量操作，如果是的需要对水印的间距进行缩放
    fileInfo: FileInfo | null | undefined;
}

export function beforeDraw(contxt: DrawContext) {
    const { ctx, dpr, width, height } = contxt;
    if (!ctx) {
        return;
    }
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);
}

export function afterDraw(contxt: DrawContext) {
    const { ctx } = contxt;
    if (!ctx) {
        return;
    }
    ctx.restore();
}

export function drawWatermark(watermark: Watermark, contxt: DrawContext) {
    if (!watermark) {
        return;
    }

    const {
        waterMarkTemplate,
        fontName,
        fontSize: originFontSize,
        fontColor,
        isBold,
        isItalic,
        isUnderline,
        isStrikethrough,
        angel,
        tran,
        stroke,
        background,
        shadow,
        position,
    } = watermark;

    if(!waterMarkTemplate) {
        return;
    }   

    const {
        width,
        height,
        ctx,
        ratio,
        isCollect,
        fileInfo,
    } = contxt;

    if (!ctx || !fileInfo) {
        return;
    }

    const waterMarkStr = getString(waterMarkTemplate, fileInfo.imageTakenTime)
    const fontSize = isCollect ? Math.round(originFontSize / ratio) : originFontSize
    const { width: watermarkWidth, height: watermarkHeight, textMetrics } = getTextWH(waterMarkStr, { fontFamily: fontName, fontSize, isItalic, isBold });

    ctx.font = `${isItalic ? 'italic' : ''} ${isBold ? 'bold' : ''} ${fontSize}px ${fontName},serif`;
    // ctx.textBaseline = 'top';
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'start';

   

    const { positionType } = position;

    if (positionType !== PositionType.Flat) {
        const { offsetLeft, offsetTop } = getPositionLeftTop({
            position,
            watermarkWidth,
            watermarkHeight,
            width,
            height,
            isCollect,
            ratio,
        })


        const waterMarkCenterX = offsetLeft + watermarkWidth / 2
        const waterMarkCenterY = offsetTop + watermarkHeight / 2
        ctx.save()
        // 以水印中心为原点 旋转
        ctx.translate(waterMarkCenterX, waterMarkCenterY);
        ctx.rotate(angel * Math.PI / 180);

        //绘制文字背景
        if (background) {
            drawBackground(contxt, -watermarkWidth / 2, -watermarkHeight / 2, watermarkWidth, watermarkHeight, background, fontSize);
        }
        //绘制文字边框
        if (stroke && stroke.strokeColor && stroke.strokeWidth > 0) {
            drawFontStroke(contxt, -watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent, waterMarkStr, stroke);
        }
        // 水印内容要在storke的上层
        drawText(contxt, -watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent, waterMarkStr, fontColor.toRgbaString(), 1 - tran / 100, shadow);
        if (isUnderline) {
            drawTextWithUnderline(ctx, -watermarkWidth / 2, watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent + textMetrics.actualBoundingBoxDescent + 3, 2, fontColor.toRgbaString());
        }
        if (isStrikethrough) {
            // 删除线的位置为这一行文字实际高度的中线
            const y = -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent - textMetrics.actualBoundingBoxAscent + (textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent) / 2;
            drawTextWithUnderline(ctx, -watermarkWidth / 2, watermarkWidth / 2, y, 2, fontColor.toRgbaString());
        }
        ctx.restore()
        return;
    }

    // 平铺
    const start = fontSize
    const distanceX = fontSize * 2 + watermarkWidth
    const distanceY = fontSize * 2 + watermarkHeight
    let i = 0;
    while (start + i * distanceX < width) {
        let j = 0;
        while (start + j * distanceY < height) {
            const curWaterMarkLeft = start + i * distanceX
            const curWaterMarkTop = start + j * distanceY
            ctx.save()
            // 以水印中心为原点 旋转
            ctx.translate(curWaterMarkLeft + watermarkWidth / 2, curWaterMarkTop + watermarkWidth / 2);
            ctx.rotate(angel * Math.PI / 180);

            //绘制文字背景
            if (background) {
                drawBackground(contxt, -watermarkWidth / 2, -watermarkHeight / 2, watermarkWidth, watermarkHeight, background, fontSize);
            }

            //绘制文字边框
            if (stroke && stroke.strokeColor && stroke.strokeWidth > 0) {
                drawFontStroke(contxt, -watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent, waterMarkStr, stroke);
            }
            drawText(contxt, -watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent, waterMarkStr, fontColor.toRgbaString(), 1 - tran / 100, shadow);

            if (isUnderline) {
                drawTextWithUnderline(ctx, -watermarkWidth / 2, watermarkWidth / 2, -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent + textMetrics.actualBoundingBoxDescent + 3, 2, fontColor.toRgbaString());
            }
            if (isStrikethrough) {
                const y = -watermarkHeight / 2 + textMetrics.fontBoundingBoxAscent - textMetrics.actualBoundingBoxAscent + (textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent) / 2;
                drawTextWithUnderline(ctx, -watermarkWidth / 2, watermarkWidth / 2, y, 2, fontColor.toRgbaString());
            }
            ctx.restore()
            j++
        }
        i++
    }
}

// 下划线函数
function drawTextWithUnderline(ctx: CanvasRenderingContext2D, x1: number, x2: number, y: number, lineWidth: number, underlineColor: string) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x1, y);
    ctx.lineTo(x2, y);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = underlineColor;
    ctx.stroke();
    ctx.restore();
}

// 绘制文字背景
function drawBackground(context: DrawContext, x: number, y: number, width: number, height: number, background: Background, fontSize: number) {
    const { ctx, ratio, isCollect } = context;
    if (!ctx || !background) {
        return;
    }

    const paddingWidth = fontSize > 40 ? 2 : 1;
    const maxRadius = Math.min(width, height) / 2;
    const backgroundRadius = isCollect ? Math.round(background.radius / ratio) : background.radius;
    const radius = Math.min(backgroundRadius, maxRadius);
    fillRoundRect(ctx, x - paddingWidth, y, width + paddingWidth * 2, height, radius, background.color.toRgbaString());
}

//绘制文字边框
function drawFontStroke(context: DrawContext, x: number, y: number, text: string, stroke: StrokeProps) {
    const { ctx, ratio, isCollect } = context
    if (!ctx) {
        return;
    }
    let strokeWidth = stroke.strokeWidth;
    if (isCollect) {
        strokeWidth = Math.round(strokeWidth / ratio)
    }
    ctx.save();
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = stroke.strokeColor.toRgbaString();
    ctx.strokeText(text, x, y);
    ctx.restore();
}

function drawText(context: DrawContext, x: number, y: number, text: string, fillColor: string, globalAlpha: number, shadow?: Shadow | null) {
    if (!fillColor) { // 此时不绘制填充色
        return;
    }
    const { ctx, ratio, isCollect } = context;
    if (!ctx) {
        return;
    }
    ctx.save();
    ctx.globalAlpha = globalAlpha;
    ctx.fillStyle = fillColor;
    if (shadow) { // 绘制阴影模糊效果
        const shadowOffsetX = isCollect ? Math.round(shadow.offsetX / ratio) : shadow.offsetX;
        const shadowOffsetY = isCollect ? Math.round(shadow.offsetY / ratio) : shadow.offsetY;

        ctx.shadowColor = shadow.shadowColor.toRgbaString();
        ctx.shadowOffsetX = shadowOffsetX;
        ctx.shadowOffsetY = shadowOffsetY;
        if (shadow.blur) {
            const shadowBlur = isCollect ? Math.round(shadow.blur / ratio) : shadow.blur;
            ctx.shadowBlur = shadowBlur;
        }
    }
    ctx.fillText(text, x, y);
    ctx.restore();
}

/**
* 绘制填充圆角矩形
* @param ctx Canvas上下文
* @param x 左上角X坐标
* @param y 左上角Y坐标
* @param width 宽度
* @param height 高度
* @param radius 圆角半径
* @param fillColor 填充颜色
*/
function fillRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fillColor: string
) {
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = fillColor;

    // 左上角
    ctx.moveTo(x + radius, y);
    // 右上角
    ctx.arcTo(x + width, y, x + width, y + radius, radius);
    // 右下角
    ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
    // 左下角
    ctx.arcTo(x, y + height, x, y + height - radius, radius);
    // 左上角
    ctx.arcTo(x, y, x + radius, y, radius);

    ctx.closePath();
    ctx.fill();
    ctx.restore();
}